import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

/**
 * #496 Split Screen Comparison - 分屏对比效果
 * 
 * 创新点：
 * 1. 动态分割线 - 从中间向两边扩展
 * 2. 双向滑入动画 - 左右内容分别入场
 * 3. 文字渐显效果 - 配合滑入动画
 * 4. 小红书风格 - 粉橙黄渐变配色
 * 
 * 适用场景：
 * - 产品对比（iPhone vs Android）
 * - 前后对比（使用前 vs 使用后）
 * - 优缺点对比（优点 vs 缺点）
 * - A/B 测试展示
 */

// 类型定义
interface SplitScreenComparisonProps {
  // 左侧内容
  leftTitle?: string;
  leftSubtitle?: string;
  leftColor?: string;
  
  // 右侧内容
  rightTitle?: string;
  rightSubtitle?: string;
  rightColor?: string;
  
  // 配置
  dividerWidth?: number; // 分割线宽度
  animationStyle?: 'slide' | 'fade' | 'zoom'; // 动画风格
}

// 小红书风格配色
const XHS_COLORS = {
  left: ['#FF6B9D', '#FF8E72'], // 粉色渐变
  right: ['#FFD93D', '#FFA726'], // 橙黄渐变
  divider: '#FFFFFF', // 白色分割线
  text: '#FFFFFF', // 白色文字
};

export const SplitScreenComparison: React.FC<SplitScreenComparisonProps> = ({
  leftTitle = '方案 A',
  leftSubtitle = '传统方法',
  leftColor,
  rightTitle = '方案 B',
  rightSubtitle = '创新方案',
  rightColor,
  dividerWidth = 4,
  animationStyle = 'slide',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  // 动画配置
  const dividerExpandDuration = 0.5; // 分割线扩展时间（秒）
  const contentDelay = 0.3; // 内容延迟时间（秒）
  const contentDuration = 0.8; // 内容动画时间（秒）
  
  // 分割线动画（从中间扩展）
  const dividerProgress = spring({
    frame,
    fps,
    config: {
      damping: 200, // 平滑无弹跳
    },
    durationInFrames: dividerExpandDuration * fps,
  });
  
  const dividerWidthAnimated = interpolate(
    dividerProgress,
    [0, 1],
    [0, dividerWidth]
  );
  
  // 左侧内容动画（延迟后滑入）
  const leftProgress = spring({
    frame: frame - contentDelay * fps,
    fps,
    config: {
      damping: 20,
      stiffness: 200, // 快速轻微弹跳
    },
    durationInFrames: contentDuration * fps,
  });
  
  // 右侧内容动画（延迟后滑入）
  const rightProgress = spring({
    frame: frame - contentDelay * fps,
    fps,
    config: {
      damping: 20,
      stiffness: 200,
    },
    durationInFrames: contentDuration * fps,
  });
  
  // 根据动画风格计算位移
  const leftTransform = animationStyle === 'slide' 
    ? interpolate(leftProgress, [0, 1], [-width / 2, 0])
    : 0;
    
  const rightTransform = animationStyle === 'slide'
    ? interpolate(rightProgress, [0, 1], [width / 2, 0])
    : 0;
  
  // 透明度动画
  const leftOpacity = interpolate(leftProgress, [0, 1], [0, 1]);
  const rightOpacity = interpolate(rightProgress, [0, 1], [0, 1]);
  
  // 缩放动画（如果选择 zoom）
  const leftScale = animationStyle === 'zoom'
    ? interpolate(leftProgress, [0, 1], [0.5, 1])
    : 1;
    
  const rightScale = animationStyle === 'zoom'
    ? interpolate(rightProgress, [0, 1], [0.5, 1])
    : 1;
  
  return (
    <AbsoluteFill style={{ backgroundColor: '#0A0A0A' }}>
      {/* 左侧区域 */}
      <AbsoluteFill
        style={{
          left: 0,
          width: width / 2,
          background: `linear-gradient(135deg, ${leftColor || XHS_COLORS.left[0]}, ${leftColor || XHS_COLORS.left[1]})`,
          transform: `translateX(${leftTransform}px) scale(${leftScale})`,
          opacity: leftOpacity,
        }}
      >
        {/* 左侧内容 */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: XHS_COLORS.text,
          }}
        >
          <h1
            style={{
              fontSize: 80,
              fontWeight: 'bold',
              margin: 0,
              opacity: leftProgress,
              transform: `scale(${interpolate(leftProgress, [0, 1], [0.8, 1])})`,
            }}
          >
            {leftTitle}
          </h1>
          <p
            style={{
              fontSize: 40,
              marginTop: 20,
              opacity: interpolate(leftProgress, [0.3, 1], [0, 1]),
            }}
          >
            {leftSubtitle}
          </p>
        </div>
      </AbsoluteFill>
      
      {/* 右侧区域 */}
      <AbsoluteFill
        style={{
          left: width / 2,
          width: width / 2,
          background: `linear-gradient(135deg, ${rightColor || XHS_COLORS.right[0]}, ${rightColor || XHS_COLORS.right[1]})`,
          transform: `translateX(${rightTransform}px) scale(${rightScale})`,
          opacity: rightOpacity,
        }}
      >
        {/* 右侧内容 */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: XHS_COLORS.text,
          }}
        >
          <h1
            style={{
              fontSize: 80,
              fontWeight: 'bold',
              margin: 0,
              opacity: rightProgress,
              transform: `scale(${interpolate(rightProgress, [0, 1], [0.8, 1])})`,
            }}
          >
            {rightTitle}
          </h1>
          <p
            style={{
              fontSize: 40,
              marginTop: 20,
              opacity: interpolate(rightProgress, [0.3, 1], [0, 1]),
            }}
          >
            {rightSubtitle}
          </p>
        </div>
      </AbsoluteFill>
      
      {/* 中间分割线 */}
      <div
        style={{
          position: 'absolute',
          left: width / 2 - dividerWidthAnimated / 2,
          top: 0,
          width: dividerWidthAnimated,
          height: height,
          backgroundColor: XHS_COLORS.divider,
          boxShadow: '0 0 20px rgba(255, 255, 255, 0.8)',
          zIndex: 10,
        }}
      />
      
      {/* VS 标志（可选） */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: 60,
          fontWeight: 'bold',
          color: XHS_COLORS.divider,
          textShadow: '0 0 10px rgba(0, 0, 0, 0.8)',
          zIndex: 20,
          opacity: interpolate(
            dividerProgress,
            [0.5, 1],
            [0, 1]
          ),
          scale: interpolate(dividerProgress, [0.5, 1], [0.5, 1]),
        }}
      >
        VS
      </div>
    </AbsoluteFill>
  );
};

// Composition 注册信息
export const SplitScreenComparisonComposition = () => {
  return (
    <SplitScreenComparison
      leftTitle="方案 A"
      leftSubtitle="传统方法"
      rightTitle="方案 B"
      rightSubtitle="创新方案"
      dividerWidth={4}
      animationStyle="slide"
    />
  );
};
